import { createGlobalResource } from './createGlobalResource';
import { Project } from 'core/models/project';
import { useEffect, useCallback } from 'react';
import { useProjects } from './useProjects';
import useLocalStorage from 'react-use/lib/useLocalStorage';
import { ignore } from 'utility/ignore';

const { withGlobalResource, useGlobalResource, useUpdateGlobalResource } = createGlobalResource<Project.Id | null>('selected-project');

type ProjectSelection = {
    projectId: Project.Id;
}

const ASSIGNED_PROJECTS_KEY = 'jp.tier4.drive.autoware.selected.project';

export { withGlobalResource as withSelectedProjectId };

export function useWithSelectedProjectId() {
    return useCallback((children: React.ReactNode) => {
        return withGlobalResource(children);
    }, []);
}

export { useWithSelectedProjectId as useWithSelectedProject };

export function useSelectProjectId() {
    const [stored, store] = useLocalStorage<ProjectSelection | null>(ASSIGNED_PROJECTS_KEY, null);
    ignore(stored);

    const updater = useCallback(
        (projectId: Project.Id | null) => {
            store(projectId !== null ? { projectId } : null);
            return projectId;
        },
        [store]
    );
    return useUpdateGlobalResource(updater);
}

export function useSelectedProjectId() {
    const value = useGlobalResource();

    const [stored] = useLocalStorage<ProjectSelection>(ASSIGNED_PROJECTS_KEY);

    const selectProjectId = useSelectProjectId();

    useEffect(() => {
        if (value === undefined && stored) {
            selectProjectId(stored.projectId);
        }
    }, [selectProjectId, stored, value]);

    return value ?? null;
}

export function useSelectedProject() {
    const projects = useProjects();
    const selectedProjectId = useSelectedProjectId();

    return selectedProjectId ? projects.find(prj => prj.id == selectedProjectId) ?? null : null;
}

export function useSelectProject() {
    const selectProjectId = useSelectProjectId();
    return useCallback((prj: Project | null) => selectProjectId(prj?.id ?? null), [selectProjectId]);
}
